import { 
  store, 
  getContext, 
  getElement,
} from '@wordpress/interactivity';
import {gsap} from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { DEFAULT_FADE_DURATION } from '../Container/v2/attributes';

gsap.registerPlugin(ScrollTrigger);

const slideClassName = `wp-block-cover`;
const activeClassName = `${slideClassName}--active`;
const pinSpacerClassName = 'pin-spacer';

function buildSlideAnimation({slides, timeline, context, rootEl}) {
    const isReveal = context.slideRevealType === 'reveal';
    const isHorizontal = context.animationDirection === 'horizontal';
    console.log(`[syncMotion.init] isReveal:`, isReveal)
    console.log(`[syncMotion.init] isHorizontal:`, isHorizontal)
    
    let startPercent, endPercent;
    if (isReveal) {
      startPercent = 0;
      endPercent = -100;
    } else {
      startPercent = 100;
      endPercent = 0;
    }

    const dimensionKey = isHorizontal
      ? 'xPercent'
      : 'yPercent';

    const pinnedSlides = isReveal
      ? slides.slice(0, -1)
      : slides.slice(1);
    console.log(`[syncMotion.init] pinnedSlides:`, pinnedSlides)

    pinnedSlides.forEach((slide) => {
      timeline.fromTo(
        slide,
        {
          [dimensionKey]: startPercent,
          ease: 'none',
        },
        {
          [dimensionKey]: endPercent,
          ease: 'none',
        },
      )
    });

    const zIndexSign = isReveal ? -1 : 1;
    gsap.set(`.${slideClassName}`, {
      zIndex: (i, target, targets) => targets.length + (i * zIndexSign)
    });
}

function buildFadeAnimation({slides, timeline, context, rootEl}) {
  const fadeDuration = context.fadeDuration || DEFAULT_FADE_DURATION;
  const fadeDurationInSeconds = fadeDuration / 1000; // Convert milliseconds to seconds
  console.debug(`[syncMotion.init] fadeDuration:`, fadeDuration, `ms`, fadeDurationInSeconds, `s`);

  gsap.set(slides, {
    opacity: 0,
  });

  const numSlides = slides.length;
  const durationPerSlide = 1 / numSlides;

  // Set the first slide visible at the start
  timeline.set(slides[0], { opacity: 1 });
  slides[0].classList.add(activeClassName);

  slides.forEach((slide, i) => {
    if (i === 0) return;

    timeline
      // Fade in current slide
      .to(
        slide, 
        {
          opacity: 1, 
          duration: fadeDurationInSeconds, 
          onStart: () => slide.classList.add(activeClassName)
        }, 
        i * durationPerSlide
      )
      // Fade out previous slide
      .to(
        slides[i - 1], 
        {
          opacity: 0, 
          duration: fadeDurationInSeconds, 
          onComplete: () => slides[i - 1].classList.remove(activeClassName)
        },
        i * durationPerSlide
      );
  });
}

const transitionAnimationTypeBuilderMap = {
  slide: buildSlideAnimation,
  fade: buildFadeAnimation,
};

const { state, actions } = store("amplify_storytelling_syncMotion", {
  state: { },

  actions: { },

  callbacks: {
    init: () => {
      console.log(`[syncMotion.init] Loading...`)
      const context = getContext();
      console.log(`[syncMotion.init] context:`, context)
      const { ref: rootEl } = getElement();
      // console.log(`[syncMotion.init] Root element:`, ref)

      let transitionAnimationType = context.animationTransitionType;
      if (!transitionAnimationType) {
        transitionAnimationType = 'slide';
      }
      console.log(`[syncMotion.init] transitionAnimationType:`, transitionAnimationType)

      // Collect slides, and perform some basic structure validation.

      const slides = Array.from(rootEl.querySelectorAll(`.${slideClassName}`));
      console.log(`[syncMotion.init] slides:`, slides)

      const innerContainers = slides
        .map((coverSlide) => {
          return coverSlide.querySelector(".wp-block-cover__inner-container");
        })
        .filter((innerContainer) => innerContainer !== null);
      console.log(`[syncMotion.init] innerContainers:`, innerContainers)

      // If no slides are found, quit initialization early.
      if (slides.length === 0) {
        console.error(`[syncMotion.init] No slides found.`);
        return;
      }
      // If the slides don't match the containers count, quit initialization early.
      if (innerContainers.length !== slides.length) {
        console.error(`[syncMotion.init] Slide composition error. Found ${slides.length} slides and ${innerContainers.length} inner containers.`);
        return;
      }

      /*
        We have three steps in setting up the animation:
          1. Set up the ScrollTrigger pinning for the root element.
          2. Copy the Gutenberg layout classes from the block element to the
            pin-spacer element.
          3. Build the animation based on the transition type.

        We must set up the ScrollTrigger pinning first, because it will create a
        new parent element around the root element, which will be used for the
        pinning. This new parent element will not have the Gutenberg layout
        classes that have been added to the block element, which can cause
        spacing and alignment issues.

        So, we need to copy the layout classes from the block element to the
        pin-spacer element before building the animation.

        Animation construction can then be performed, specific to each type
        of transition.
      */

      // Set up the container pinning.
      let timeline = gsap.timeline({
        scrollTrigger: {
          trigger: rootEl,
          start: "top top",
          end: `+=${slides.length * 100}%`,
          scrub: true,
          pin: true,
          // pinSpacing: true, 
          markers: false,
        }
      });

      /*
        ScrollTrigger pinning introduces a new div.pin-spacer parent around the 
        pinned element (in this case, the element pointed to by `ref`).
        This pin-spacer element lacks the Gutenberg layout classes that have 
        been added to the block element, which in turn causes the block to have
        spacing and alignment issues.
        To fix this, we'll copy any of the Gutenberg layout classes from the 
        block element to the pin-spacer element.
      */
      const layoutClasses = [
        'alignfull',
        'alignwide',
        'alignleft',
        'alignright',
        'is-layout-flow',
        // TODO: there may be others to add...
      ];
      const pinSpacer = rootEl.closest(`.${pinSpacerClassName}`);

      // If the pin-spacer element can't be found, something went wrong, so
      // let's log an error and quit early.
      if (!pinSpacer) {
        console.error(`[syncMotion.init] pin-spacer element not found. Aborting initialization.`);
        return;
      }

      layoutClasses.forEach((layoutClass) => {
        if (rootEl.classList.contains(layoutClass)) {
          pinSpacer.classList.add(layoutClass);
        }
      });

      // Build the animation based on the transition type.
      const animationBuilder = transitionAnimationTypeBuilderMap[transitionAnimationType];
      if (!animationBuilder) {
        console.error(`[syncMotion.init] Unsupported transition type: ${transitionAnimationType}. Aborting initialization.`);
        return;
      }

      animationBuilder({
        slides,
        timeline,
        context,
        rootEl,
      });
    },
  },
});
